import {
  createContext,
  PropsWithChildren,
  useContext,
  useMemo,
  useState,
} from 'react';

import { noop } from 'lodash-es';

import { FakeParticipantsPanel } from '@/components/FakeParticipantsPanel';
import { FeatureFlagsDialog } from '@/components/FeatureFlagsDialog';
import { FeatureFlagGate } from '@/config/featureFlags/FeatureFlagProvider';
import { useDevMode } from '@/hooks/useDevMode';
import { useKeyboardShortcut } from '@/hooks/useKeyboardShortcut';

interface DevModeContextValue {
  isDevMode: boolean;
  handleDevModeClick: () => void;
  enableDevMode: () => void;
  disableDevMode: () => void;
  toggleDevMode: () => void;
}

const DevModeContext = createContext<DevModeContextValue>({
  isDevMode: false,
  handleDevModeClick: noop,
  enableDevMode: noop,
  disableDevMode: noop,
  toggleDevMode: noop,
});

export const useDevModeContext = () => useContext(DevModeContext);

/**
 * Global DevMode Provider
 * Provides dev mode functionality and feature flags dialog across the entire app
 * Only enabled for non-web platforms (Zoom, Teams, Webex, Meet)
 */
export const DevModeProvider = ({ children }: PropsWithChildren) => {
  const {
    isDevMode,
    handleDevModeClick,
    enableDevMode,
    disableDevMode,
    toggleDevMode,
  } = useDevMode();

  const [showFeatureFlagsDialog, setShowFeatureFlagsDialog] = useState(false);

  // Global keyboard shortcut: Cmd+Shift+F to open feature flags dialog
  useKeyboardShortcut(
    {
      key: 'f',
      metaKey: true,
      shiftKey: true,
      enabled: isDevMode,
    },
    () => {
      setShowFeatureFlagsDialog(true);
    },
  );

  const contextValue = useMemo(
    () => ({
      isDevMode,
      handleDevModeClick,
      enableDevMode,
      disableDevMode,
      toggleDevMode,
    }),
    [
      isDevMode,
      handleDevModeClick,
      enableDevMode,
      disableDevMode,
      toggleDevMode,
    ],
  );

  return (
    <DevModeContext.Provider value={contextValue}>
      {children}

      {/* Global feature flags dialog - available on any page when dev mode is active */}
      {isDevMode && (
        <FeatureFlagsDialog
          open={showFeatureFlagsDialog}
          onOpenChange={setShowFeatureFlagsDialog}
        />
      )}

      {/* Fake participants panel - available when dev mode is active and flag is enabled */}
      {isDevMode && (
        <FeatureFlagGate flag="FAKE_PARTICIPANTS">
          <FakeParticipantsPanel />
        </FeatureFlagGate>
      )}
    </DevModeContext.Provider>
  );
};
